import classnames from "classnames";
import { ChevronLeft, ChevronRight, List, PanelRightClose } from "lucide-react";

import { useTranslation } from "@/lib/i18n";
import panelCommonStyles from "@/features/shared/widgets/map/controls/panel-common.module.css";
import { ControlToggle } from "@/features/shared/widgets/map/map.styles";
import {
  normalizeWarningLevel,
  WarningLevel,
  warningLevelColorMap,
} from "@/lib/warning-level";

import { MapRegion } from "./maritime-warnings.model";
import {
  formatWarningLevelBadge,
  getAreaName,
  getWarningDescription,
} from "./maritime-warnings.utils";
import badgeStyles from "./warning-level-badge.module.css";
import styles from "./warnings-right-panel.module.css";

type WarningsRightPanelProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  visibleRegions: MapRegion[];
  selectedRegion: MapRegion | undefined;
  contentLanguage: "nl" | "en";
  onSelectRegion: (areaId: string) => void;
  onClose: () => void;
};

const LEGEND_GRAY = "rgba(0, 0, 0, 0.2)";

function worstRegionLevel(region: MapRegion): WarningLevel {
  return region.warnings.reduce<WarningLevel>(
    (worst, w) => Math.max(worst, normalizeWarningLevel(w.level)) as WarningLevel,
    WarningLevel.Green,
  );
}

const WarningsRightPanel = ({
  isOpen,
  onOpenChange,
  visibleRegions,
  selectedRegion,
  contentLanguage,
  onSelectRegion,
  onClose,
}: WarningsRightPanelProps) => {
  const { t } = useTranslation();

  if (!isOpen) {
    return (
      <div className={styles.collapsedToggle}>
        <ControlToggle
          type="button"
          active={false}
          aria-label={t("dashboard.warnings.interactiveMap.sidePanel.openAria")}
          onClick={() => onOpenChange(true)}
        >
          <List size={20} />
        </ControlToggle>
      </div>
    );
  }

  return (
    <div
      className={classnames(
        styles.panel,
        panelCommonStyles.frostedPanel,
        panelCommonStyles.mapPanel,
      )}
    >
      {selectedRegion ? (
        <DetailsView
          region={selectedRegion}
          contentLanguage={contentLanguage}
          onClose={onClose}
          onCollapse={() => onOpenChange(false)}
        />
      ) : (
        <ListView
          regions={visibleRegions}
          contentLanguage={contentLanguage}
          onSelectRegion={onSelectRegion}
          onCollapse={() => onOpenChange(false)}
        />
      )}
    </div>
  );
};

export default WarningsRightPanel;

// ---- List view ----------------------------------------------------------------

type ListViewProps = {
  regions: MapRegion[];
  contentLanguage: "nl" | "en";
  onSelectRegion: (areaId: string) => void;
  onCollapse: () => void;
};

function ListView({ regions, contentLanguage, onSelectRegion, onCollapse }: ListViewProps) {
  const { t } = useTranslation();
  const sorted = [...regions].sort((a, b) => worstRegionLevel(b) - worstRegionLevel(a));

  return (
    <>
      <div className={styles.header}>
        <div>
          <p className={styles.headerTitle}>
            {t("dashboard.warnings.interactiveMap.sidePanel.title")}
          </p>
          <p className={styles.headerSubtitle}>
            {t("dashboard.warnings.interactiveMap.sidePanel.subtitle")}
          </p>
        </div>
        <button
          aria-label={t("dashboard.warnings.interactiveMap.sidePanel.closeAria")}
          className={styles.headerButton}
          type="button"
          onClick={onCollapse}
        >
          <PanelRightClose size={18} />
        </button>
      </div>
      <div className={classnames(panelCommonStyles.panelDivider, styles.headerDivider)} />
      <div className={styles.body}>
        <ul className={styles.regionList}>
          {sorted.map((region) => (
            <li key={region.area.id}>
              <RegionCard
                region={region}
                contentLanguage={contentLanguage}
                onSelect={onSelectRegion}
              />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

type RegionCardProps = {
  region: MapRegion;
  contentLanguage: "nl" | "en";
  onSelect: (areaId: string) => void;
};

function RegionCard({ region, contentLanguage, onSelect }: RegionCardProps) {
  const { t } = useTranslation();
  const level = worstRegionLevel(region);
  const badgeColor =
    level === WarningLevel.Green ? LEGEND_GRAY : warningLevelColorMap[level];

  const mostSevere = [...region.warnings].sort(
    (a, b) => normalizeWarningLevel(b.level) - normalizeWarningLevel(a.level),
  )[0];

  const description = mostSevere
    ? getWarningDescription(mostSevere, contentLanguage)
    : t("dashboard.warnings.interactiveMap.sidePanel.noWarnings");

  return (
    <button className={styles.regionCard} onClick={() => onSelect(region.area.id)}>
      <div className={styles.regionCardTop}>
        <span className={badgeStyles.block}>
          <span className={badgeStyles.dot} style={{ background: badgeColor }} />
          <span className={badgeStyles.label}>
            {formatWarningLevelBadge(level, contentLanguage)}
          </span>
        </span>
        <span className={styles.regionName}>
          {getAreaName(region.area, contentLanguage)}
        </span>
        <ChevronRight className={styles.regionChevron} size={16} />
      </div>
      <p className={styles.regionDescription}>{description}</p>
    </button>
  );
}

// ---- Details view -------------------------------------------------------------

type DetailsViewProps = {
  region: MapRegion;
  contentLanguage: "nl" | "en";
  onClose: () => void;
  onCollapse: () => void;
};

function DetailsView({ region, contentLanguage, onClose, onCollapse }: DetailsViewProps) {
  const { t } = useTranslation();
  const sortedWarnings = [...region.warnings].sort(
    (a, b) => normalizeWarningLevel(b.level) - normalizeWarningLevel(a.level),
  );

  return (
    <>
      <div className={styles.header}>
        <p className={styles.headerTitle}>{getAreaName(region.area, contentLanguage)}</p>
        <div className={styles.headerActions}>
          <button
            aria-label={t("dashboard.warnings.interactiveMap.closeDetailsAria")}
            className={styles.headerButton}
            type="button"
            onClick={onClose}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            aria-label={t("dashboard.warnings.interactiveMap.sidePanel.closeAria")}
            className={styles.headerButton}
            type="button"
            onClick={onCollapse}
          >
            <PanelRightClose size={18} />
          </button>
        </div>
      </div>
      <div className={classnames(panelCommonStyles.panelDivider, styles.headerDivider)} />
      <div className={styles.body}>
        {sortedWarnings.length === 0 ? (
          <p className={styles.emptyWarnings}>
            {t("dashboard.warnings.interactiveMap.noWarnings")}
          </p>
        ) : (
          <ul className={styles.warningList}>
            {sortedWarnings.map((warning) => (
              <li key={warning.id} className={styles.warningRow}>
                <span className={badgeStyles.block}>
                  <span
                    className={badgeStyles.dot}
                    style={{
                      backgroundColor:
                        warningLevelColorMap[normalizeWarningLevel(warning.level)],
                    }}
                  />
                  <span className={badgeStyles.label}>
                    {formatWarningLevelBadge(
                      normalizeWarningLevel(warning.level),
                      contentLanguage,
                    )}
                  </span>
                </span>
                <span className={styles.warningPhenomenon}>{warning.phenomenon}</span>
                <span className={styles.warningMessage}>
                  {getWarningDescription(warning, contentLanguage)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
